import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { IStorePackage } from 'models/store/i-store-package.interface';
import { ITradeAsset } from 'models/trade/i-trade-details.interface';

import { addErrorMessageAction } from 'actions/global-actions';

import { fetchAllAssets } from 'services/market.service';

import { queryKeys } from './query-keys';

export interface ITradeAssetWithAmount extends ITradeAsset {
  packageAmount: number; // Amount of this asset in the package
}

// Fetch package assets and highlighted assets
const fetchPackageAssets = async (
  packageData: IStorePackage
): Promise<{
  assets: ITradeAssetWithAmount[];
  highlightedAssets: ITradeAssetWithAmount[];
}> => {
  // Collect all unique asset IDs from both items and highlighted_assets
  const itemAssetIds = packageData?.items?.map((item) => item.asset_id) || [];
  const highlightedAssetIds = packageData?.highlighted_assets?.map((item) => item.asset_id) || [];
  const allAssetIds = [...new Set([...itemAssetIds, ...highlightedAssetIds])];

  if (allAssetIds.length === 0) {
    return { assets: [], highlightedAssets: [] };
  }

  // Fetch all assets data using the existing service
  const response = await fetchAllAssets(allAssetIds);

  if (!response.data) {
    return { assets: [], highlightedAssets: [] };
  }

  // Combine asset data with package amount information for items
  const assets: ITradeAssetWithAmount[] = response.data
    .filter((asset: ITradeAsset) => itemAssetIds.includes(asset.id))
    .map((asset: ITradeAsset) => {
      const packageItem = packageData.items.find((item) => item.asset_id === asset.id);
      return {
        ...asset,
        packageAmount: packageItem?.amount || 1,
      };
    });

  // Combine asset data with package amount information for highlighted assets
  const highlightedAssets: ITradeAssetWithAmount[] = response.data
    .filter((asset: ITradeAsset) => highlightedAssetIds.includes(asset.id))
    .map((asset: ITradeAsset) => {
      const packageItem = packageData.highlighted_assets.find((item) => item.asset_id === asset.id);
      return {
        ...asset,
        packageAmount: packageItem?.amount || 1,
      };
    });

  return { assets, highlightedAssets };
};

export interface UsePackageAssetsQueryReturn {
  assets: ITradeAssetWithAmount[];
  highlightedAssets: ITradeAssetWithAmount[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export const usePackageAssetsQuery = (packageData: IStorePackage | null): UsePackageAssetsQueryReturn => {
  const dispatch = useDispatch();

  // Get asset IDs for query key (combine items and highlighted assets)
  const itemAssetIds = packageData?.items?.map((item) => item.asset_id) || [];
  const highlightedAssetIds = packageData?.highlighted_assets?.map((item) => item.asset_id) || [];
  const allAssetIds = [...new Set([...itemAssetIds, ...highlightedAssetIds])];

  // Query for package assets
  const {
    data = { assets: [], highlightedAssets: [] },
    isLoading: loading,
    error,
    refetch: refresh,
  } = useQuery({
    queryKey: queryKeys.assets.list(allAssetIds),
    queryFn: () => fetchPackageAssets(packageData!),
    enabled: !!packageData && (packageData.items?.length > 0 || packageData.highlighted_assets?.length > 0),
  });

  // Handle errors
  useEffect(() => {
    if (error) {
      const errorMessage =
        (error as any)?.response?.data?.message || error?.message || 'Failed to fetch package assets';
      dispatch(addErrorMessageAction(errorMessage));
    }
  }, [error, dispatch]);

  return {
    assets: data.assets,
    highlightedAssets: data.highlightedAssets,
    loading,
    error: error ? String(error) : null,
    refresh,
  };
};
